import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/modules/auth/useAuth';
import { useProfileBuilder } from '@/modules/auth/useProfileBuilder';
import { useIsModerator } from '@/modules/admin/useIsModerator';
import { useAgeGate } from '@/modules/auth/useAgeGate';
import { ROUTES } from '@/config/routes';

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * When `true` (default), authenticated users with an incomplete profile
   * are bounced to `/profile-builder`. Set `false` for routes that the
   * profile builder itself or any auth-only setup screen uses.
   */
  requireComplete?: boolean;
  /**
   * When `true`, the route additionally requires membership in
   * `public.hottr_moderators`. Used for /admin/* surfaces.
   */
  requireModerator?: boolean;
  /**
   * When `true` (default), the route requires a passed hard age-gate.
   * Enforcement is additionally gated by the `age_gate` feature flag — when
   * the flag is off (or the table/backend is absent) this is a no-op.
   */
  requireAgeVerified?: boolean;
}

/**
 * Route guard wrapping any protected surface.
 *
 *   - Not authenticated → Navigate to `/` (landing)
 *   - Authenticated, profile incomplete, requireComplete → `/profile-builder`
 *   - requireModerator and not a moderator → `/feed`
 *   - Otherwise → children
 */
export const ProtectedRoute = ({
  children,
  requireComplete = true,
  requireModerator = false,
  requireAgeVerified = true,
}: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const { isComplete } = useProfileBuilder();
  const { loading: modLoading, isModerator } = useIsModerator();
  const ageGate = useAgeGate();
  const location = useLocation();

  if (loading) {
    return null;
  }

  if (!user) {
    return <Navigate to={ROUTES.landing} replace state={{ from: location.pathname }} />;
  }

  // Hard age-gate — only enforced when the `age_gate` flag is on AND the
  // backing table/backend exist (useAgeGate degrades to canAccess otherwise).
  if (requireAgeVerified) {
    if (ageGate.loading) return null;
    if (!ageGate.canAccess) return <Navigate to={ROUTES.verifyAge} replace />;
  }

  if (requireComplete && !isComplete) {
    return <Navigate to={ROUTES.profileBuilder} replace />;
  }

  if (requireModerator) {
    if (modLoading) return null;
    if (!isModerator) return <Navigate to={ROUTES.feed} replace />;
  }

  return <>{children}</>;
};
